import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Stack, Button, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  ToggleButton, ToggleButtonGroup, Alert, Grid,
} from '@mui/material';
import {
  TableRestaurant as TableIcon, Add as AddIcon, Edit as EditIcon,
  Delete as DeleteIcon, AutoFixHigh as AutoIcon,
} from '@mui/icons-material';
import PaginatedTable from '../components/common/PaginatedTable';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { tablesApi, TableZone, TABLE_ZONES } from '../api/tables.api';

const ZONE_COLORS: Record<TableZone, 'primary' | 'secondary' | 'default'> = {
  salon: 'primary',
  patio: 'secondary',
  otros: 'default',
};

// Regla del spec RF-01: 1-20=salon, 21-40=patio, 41+=otros.
const defaultZoneFor = (n: number): TableZone => {
  if (n >= 1 && n <= 20) return 'salon';
  if (n >= 21 && n <= 40) return 'patio';
  return 'otros';
};

interface TableRow {
  id: number;
  number: number;
  name: string;
  capacity: number;
  status: 'libre' | 'ocupada' | 'reservada';
  zone: TableZone;
}

const Tables: React.FC = () => {
  // Permite entrar desde sidebar con la zona pre-filtrada: /mesas?zone=salon.
  // El filtro se deriva SIEMPRE de la URL para evitar desincronizaciones
  // entre el state local y la navegacion del sidebar.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawZone = searchParams.get('zone');
  const zoneFilter: TableZone | 'todas' =
    rawZone === 'salon' || rawZone === 'patio' || rawZone === 'otros' ? rawZone : 'todas';

  const [rows, setRows] = useState<TableRow[]>([]);
  const [loading, setLoading] = useState(true);

  const changeZone = (z: TableZone | 'todas') => {
    if (z === 'todas') setSearchParams({});
    else setSearchParams({ zone: z });
  };

  // Form CRUD
  const [editing, setEditing] = useState<TableRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState('4');
  const [zone, setZone] = useState<TableZone>('salon');

  // Eliminar
  const [deleting, setDeleting] = useState<TableRow | null>(null);

  // Reasignar masivo
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassigning, setReassigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tablesApi.getAll();
      setRows((res.data || []) as TableRow[]);
    } catch (err) {
      console.error('Error cargando mesas:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = async () => {
    setEditing(null);
    // Re-fetcheamos para evitar que sugerimos un numero que otra sesion ya creo.
    let fresh = rows;
    try {
      const r = await tablesApi.getAll();
      fresh = (r.data || []) as TableRow[];
      setRows(fresh);
    } catch {}

    const used = new Set(fresh.map((t) => t.number));
    // Si el filtro actual es una zona especifica, buscamos el primer numero
    // libre dentro del rango. Si no, buscamos el primer hueco global.
    let nextNumber: number;
    if (zoneFilter !== 'todas') {
      const ranges: Record<TableZone, [number, number]> = {
        salon: [1, 20], patio: [21, 40], otros: [41, 999],
      };
      const [from, to] = ranges[zoneFilter];
      let n = from;
      while (n <= to && used.has(n)) n++;
      nextNumber = n;
    } else {
      let n = 1;
      while (used.has(n)) n++;
      nextNumber = n;
    }
    setNumber(String(nextNumber));
    setName(`Mesa ${nextNumber}`);
    setCapacity('4');
    setZone(zoneFilter === 'todas' ? defaultZoneFor(nextNumber) : zoneFilter);
    setFormOpen(true);
  };

  const openEdit = (row: TableRow) => {
    setEditing(row);
    setNumber(String(row.number));
    setName(row.name);
    setCapacity(String(row.capacity));
    setZone(row.zone || 'salon');
    setFormOpen(true);
  };

  // Si el cajero cambia el numero y la zona estaba en default segun el rango,
  // re-sugerir la zona del rango nuevo. No pisamos si ya la cambio a mano.
  const handleNumberChange = (v: string) => {
    setNumber(v);
    const n = parseInt(v, 10);
    if (!editing && !isNaN(n) && n > 0) {
      setZone(defaultZoneFor(n));
    }
  };

  const submit = async () => {
    const n = parseInt(number, 10);
    if (isNaN(n) || n <= 0) { alert('Numero de mesa invalido.'); return; }
    if (!name.trim()) { alert('Nombre obligatorio.'); return; }
    const cap = parseInt(capacity, 10);
    if (isNaN(cap) || cap <= 0) { alert('Capacidad invalida.'); return; }
    setSubmitting(true);
    try {
      const payload = { number: n, name: name.trim(), capacity: cap, zone };
      if (editing) await tablesApi.update(editing.id, payload);
      else await tablesApi.create(payload);
      setFormOpen(false);
      load();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'No se pudo guardar la mesa.';
      alert(Array.isArray(msg) ? msg.join(', ') : msg);
      // Refrescar para que la proxima sugerencia este al dia.
      load();
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setSubmitting(true);
    try {
      await tablesApi.delete(deleting.id);
      setDeleting(null);
      load();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'No se pudo eliminar la mesa.');
    } finally {
      setSubmitting(false);
    }
  };

  // Asigna a cada mesa su zona segun el rango del spec (sobrescribe la actual).
  const reassignByRange = async () => {
    setReassigning(true);
    try {
      const updates = rows
        .map((r) => ({ id: r.id, target: defaultZoneFor(r.number), current: r.zone }))
        .filter((u) => u.target !== u.current);
      for (const u of updates) {
        await tablesApi.update(u.id, { zone: u.target });
      }
      setReassignOpen(false);
      load();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Error reasignando.');
    } finally {
      setReassigning(false);
    }
  };

  const visibles = zoneFilter === 'todas' ? rows : rows.filter((r) => r.zone === zoneFilter);

  // Conteos por zona para los chips.
  const counts: Record<string, number> = { todas: rows.length };
  for (const z of TABLE_ZONES) counts[z.key] = rows.filter((r) => r.zone === z.key).length;

  if (loading && rows.length === 0) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TableIcon sx={{ fontSize: 32, color: '#D97706' }} />
        <Typography variant="h4">
          {zoneFilter === 'todas'
            ? 'Mesas'
            : `Mesas — ${TABLE_ZONES.find((z) => z.key === zoneFilter)?.label}`}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" startIcon={<AutoIcon />} onClick={() => setReassignOpen(true)}>
          Reasignar zonas por rango
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Nueva mesa
        </Button>
      </Box>
      <Typography variant="body2" sx={{ mb: 2, color: '#6B7280' }}>
        Una mesa por zona: <b>Salon (1-20)</b>, <b>Patio (21-40)</b>, <b>Otros (41+)</b>. El sistema
        sugiere la zona segun el numero, pero podes overridearla manualmente.
      </Typography>

      {/* KPIs por zona */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper sx={{ p: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>Total mesas</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{counts.todas}</Typography>
          </Paper>
        </Grid>
        {TABLE_ZONES.map((z) => (
          <Grid key={z.key} size={{ xs: 6, md: 3 }}>
            <Paper sx={{ p: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>{z.label}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{counts[z.key]}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filtros por zona */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Chip
          label={`Todas (${counts.todas})`}
          color={zoneFilter === 'todas' ? 'primary' : 'default'}
          onClick={() => changeZone('todas')}
        />
        {TABLE_ZONES.map((z) => (
          <Chip
            key={z.key}
            label={`${z.label} (${counts[z.key]})`}
            color={zoneFilter === z.key ? 'primary' : 'default'}
            onClick={() => changeZone(z.key)}
          />
        ))}
      </Stack>

      <PaginatedTable
        columns={[
          { id: 'number', label: 'N°', minWidth: 60 },
          { id: 'name', label: 'Nombre' },
          // Columna capacidad oculta temporalmente.
          // { id: 'capacity', label: 'Capacidad', minWidth: 90 },
          {
            id: 'zone',
            label: 'Zona',
            format: (v: TableZone) => (
              <Chip
                size="small"
                label={TABLE_ZONES.find((z) => z.key === v)?.label || v}
                color={ZONE_COLORS[v] || 'default'}
              />
            ),
          },
          {
            id: 'status',
            label: 'Estado',
            format: (v: string) => (
              <Chip
                size="small" label={v}
                color={v === 'libre' ? 'success' : v === 'ocupada' ? 'warning' : 'default'}
              />
            ),
          },
          {
            id: 'actions',
            label: 'Acciones',
            format: (_v: any, r: TableRow) => (
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" color="primary" onClick={() => openEdit(r)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => setDeleting(r)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ),
          },
        ] as any}
        rows={visibles}
      />

      {/* Modal crear/editar */}
      <Dialog open={formOpen} onClose={() => !submitting && setFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? `Editar Mesa #${editing.number}` : 'Nueva mesa'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Numero" type="number" fullWidth size="small"
              value={number} onChange={(e) => handleNumberChange(e.target.value)}
              disabled={!!editing}
              helperText={editing ? 'No se puede cambiar el numero de una mesa existente.' : 'Sugerido segun mesas ya creadas.'}
            />
            <TextField
              label="Nombre" fullWidth size="small"
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Mesa 1 / Reservado VIP"
            />
            {/* Campo capacidad oculto temporalmente — se guarda en '4' por default */}
            <Box>
              <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5 }}>
                Zona
              </Typography>
              <ToggleButtonGroup
                value={zone} exclusive fullWidth size="small"
                onChange={(_e, v) => v && setZone(v)}
              >
                {TABLE_ZONES.map((z) => (
                  <ToggleButton key={z.key} value={z.key}>{z.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFormOpen(false)} disabled={submitting}>Cancelar</Button>
          <Button variant="contained" onClick={submit} disabled={submitting}>
            {submitting ? 'Guardando...' : editing ? 'Guardar cambios' : 'Crear mesa'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal eliminar */}
      <Dialog open={!!deleting} onClose={() => !submitting && setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar mesa</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning">
            Vas a eliminar <b>{deleting?.name}</b>. Si tiene pedidos historicos vinculados,
            podria fallar la operacion.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleting(null)} disabled={submitting}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={submitting}>
            {submitting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal reasignar masivo */}
      <Dialog open={reassignOpen} onClose={() => !reassigning && setReassignOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reasignar zonas por rango</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 1 }}>
            Pone la zona de cada mesa segun la regla del spec:
            <ul style={{ margin: 4, paddingLeft: 18 }}>
              <li>Mesas 1-20 → <b>Salon</b></li>
              <li>Mesas 21-40 → <b>Patio</b></li>
              <li>Mesas 41+ → <b>Otros</b></li>
            </ul>
            Se sobrescriben las zonas configuradas a mano. Las que ya coinciden no se tocan.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReassignOpen(false)} disabled={reassigning}>Cancelar</Button>
          <Button variant="contained" onClick={reassignByRange} disabled={reassigning}>
            {reassigning ? 'Reasignando...' : 'Confirmar reasignacion'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tables;
